import { Button, Flex } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import React from "react";
import { Card } from "src/components/Layout/Card";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useEditFormTemplateContext } from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/context";
import {
  FormType,
  getQuestionTypeFormValue,
  toFormValues,
  updateQuestion,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/formik";
import { useFormValidation } from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/hooks/useFormValidation";
import * as Draft from "src/scenes/orgAdmin/formTemplates/types/draft";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { QuestionVisibility } from "../../QuestionVisibility";
import { ConfigureQuestion } from "../QuestionConfiguration/ConfigureQuestion";
import { SchoolItem } from "../types";
import { AddressQuestionForm } from "./AddressQuestion";
import { AnswersOptions } from "./AnswersOptions";
import { GradeQuestionForm } from "./GradeQuestion";
import { PermissionLevel } from "./PermissionLevel";
import { QuestionConstraints, QuestionFormat } from "./QuestionContraints";
import { QuestionKey } from "./QuestionKey";
import { QuestionLink } from "./QuestionLink";
import { QuestionTitle } from "./QuestionTitle";
import { QuestionType } from "./QuestionType";

export type QuestionFormEditProps = {
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  onCancelButtonClick: () => void;
  onUpdate: (question: Draft.Question) => Promise<void>;
  question: Draft.Question;
  sectionType: AF.SectionType;
  gradesConfig: GQL.GetGradesConfigByOrganization_grade_config[];
  otherQuestions: readonly Draft.Question[];
};
export function QuestionFormEdit({
  onCancelButtonClick,
  onUpdate,
  schools,
  question,
  sectionType,
  gradesConfig,
  otherQuestions,
}: QuestionFormEditProps) {
  const flags = useFlags(["question-visibility-admin"]);
  const initialValues: FormType = React.useMemo(
    () => toFormValues(question, flags["question-visibility-admin"].enabled),
    [flags, question]
  );
  const {
    grades,
    formTemplateVersion,
    verificationOptions: verificationOptionsRD,
  } = useEditFormTemplateContext();

  const verificationOptions = React.useMemo(
    () => (verificationOptionsRD.hasData() ? verificationOptionsRD.data : []),
    [verificationOptionsRD]
  );

  const validate = useFormValidation({
    isV2FormTemplate: formTemplateVersion === "2.0.0",
    verificationOptions,
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const toast = useAvelaToast();

  const onSubmit = React.useCallback(
    async (values: FormType) => {
      if (!grades.hasData()) {
        throw new Error("Missing grades");
      }
      setIsSubmitting(true);
      try {
        await onUpdate(
          updateQuestion(
            question,
            values,
            verificationOptions,
            grades.data,
            flags["question-visibility-admin"].enabled
          )
        );
      } catch (err: unknown) {
        console.error(err);
        toast.error({
          title: "Oops! Something went wrong",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [onUpdate, question, toast, verificationOptions, grades, flags]
  );

  return (
    <Card display="flex" flexDirection="column" showBorder gap="3" padding={6}>
      <Formik<FormType>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {(formik) => {
          const questionType = getQuestionTypeFormValue(
            question.id,
            formik.values
          );

          const questionTypeTouched =
            formik.touched.questionTypes?.[question.id];
          const questionTypeError = formik.errors.questionTypes?.[question.id];

          let questionForm;
          switch (questionType) {
            case AF.GradesType:
              questionForm = (
                <GradeQuestionForm
                  sectionType={sectionType}
                  questionId={question.id}
                  gradesConfig={gradesConfig}
                  otherQuestions={otherQuestions}
                  schools={schools}
                  verificationOptions={verificationOptions}
                />
              );
              break;

            case AF.AddressType:
              questionForm = (
                <AddressQuestionForm
                  questionId={question.id}
                  verificationOptions={verificationOptions}
                  schools={schools}
                  sectionType={sectionType}
                  otherQuestions={otherQuestions}
                />
              );
              break;

            default:
              questionForm = (
                <>
                  <Flex direction="row" gap="4">
                    <ConfigureQuestion
                      questionId={question.id}
                      questionType={questionType}
                      verificationOptions={verificationOptions}
                    />
                  </Flex>
                  <Flex direction="column" flexGrow="1" gap="3">
                    <QuestionLink questionId={question.id} />
                    <PermissionLevel questionId={question.id} />
                    <QuestionConstraints
                      questionId={question.id}
                      questionType={question.type}
                    />
                    <QuestionVisibility
                      sectionType={sectionType}
                      questionId={question.id}
                      otherQuestions={otherQuestions}
                      schools={schools}
                    />
                    <AnswersOptions
                      sectionType={sectionType}
                      questionId={question.id}
                      schools={schools}
                      verificationOptions={verificationOptions}
                      question={question}
                      otherQuestions={otherQuestions}
                    />
                  </Flex>
                </>
              );
              break;
          }
          return (
            <Flex as={Form} direction="column" gap="6">
              <QuestionTitle questionId={question.id} />
              <QuestionKey
                questionId={question.id}
                otherQuestions={otherQuestions}
              />
              <QuestionType
                isDisabled={question.isNew !== true}
                questionId={question.id}
                sectionType={sectionType}
                error={questionTypeTouched ? questionTypeError : undefined}
              />
              <QuestionFormat
                questionId={question.id}
                questionType={question.type}
              />

              {questionForm}

              <Flex gap="3" justifyContent="right" paddingY="3">
                <Button
                  variant="ghost"
                  colorScheme="gray"
                  onClick={onCancelButtonClick}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={!formik.isValid || !formik.dirty}
                >
                  Update question
                </Button>
              </Flex>
            </Flex>
          );
        }}
      </Formik>
    </Card>
  );
}
